import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { ProductionRecord, ServiceCutoff, MeterType, Role } from '../types';
import { predictionService, LINE_COEFFICIENTS } from '../services/predictionService';
import Card from '../components/common/Card';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

// Subcomponentes modulares
import { CalendarHeader } from '../components/calendar/CalendarHeader';
import { ModulationChartCard } from '../components/calendar/ModulationChartCard';
import { KpiAnalysisTable } from '../components/calendar/KpiAnalysisTable';
import { ShiftDetailModal } from '../components/calendar/ShiftDetailModal';
import { WeeklyPlannerModal } from '../components/calendar/WeeklyPlannerModal';

// Helpers y SVGs compartidos
import { 
  getWeekNumber, 
  getDaysOfWeek, 
  formatDateStr, 
  getDurationInHours,
  ServiceIcon,
  EventIcon,
  PendingAlertSVG,
  SuccessCheckSVG
} from '../components/calendar/calendarHelpers';

export const ProductionCalendar: React.FC = () => {
  const { stopRecords, meters, currentUser } = useContext(AppContext);
  
  // Nivel de permisos
  const isEditor = currentUser?.role === Role.ADMIN || currentUser?.role === Role.SUPERVISOR;

  // Estados de Vista
  const [calendarView, setCalendarView] = useState<'month' | 'week'>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedWeek, setSelectedWeek] = useState<number>(() => getWeekNumber(new Date()));
  const [selectedYear, setSelectedYear] = useState<number>(() => new Date().getFullYear());
  const [activeTab, setActiveTab] = useState<'calendar' | 'charts'>('calendar');
  const [selectedService, setSelectedService] = useState<'electricity' | 'water' | 'steam' | 'co2'>('electricity');

  // Estados de carga e historial
  const [productionRecords, setProductionRecords] = useState<ProductionRecord[]>([]);
  const [serviceCutoffs, setServiceCutoffs] = useState<ServiceCutoff[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estados del Modal de Edición Diaria
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Estados del Planificador Semanal de Cortes
  const [isWeeklyModalOpen, setIsWeeklyModalOpen] = useState(false);
  
  // Formulario producción
  const [l1Projected, setL1Projected] = useState<number>(0);
  const [l1Real, setL1Real] = useState<number>(0);
  const [l1PlannedEvents, setL1PlannedEvents] = useState<string[]>([]);
  const [l1RealEvents, setL1RealEvents] = useState<string[]>([]);
  
  const [l3Projected, setL3Projected] = useState<number>(0);
  const [l3Real, setL3Real] = useState<number>(0);
  const [l3PlannedEvents, setL3PlannedEvents] = useState<string[]>([]);
  const [l3RealEvents, setL3RealEvents] = useState<string[]>([]);

  const [weeklyVolumes, setWeeklyVolumes] = useState<Record<string, { L1: number; L3: number }>>({});
  const [chartViewMode, setChartViewMode] = useState<'day' | 'week' | 'month'>('week');
  const [chartDayFocus, setChartDayFocus] = useState<string>(() => {
    const today = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  });

  // Rango de fechas a consultar en Supabase
  const { startFetchStr, endFetchStr } = useMemo(() => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    if (calendarView === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const end = new Date(year, month + 1, 0);
      return {
        startFetchStr: `${year}-${pad(month + 1)}-01`,
        endFetchStr: `${year}-${pad(month + 1)}-${pad(end.getDate())}`
      };
    } else {
      const days = getDaysOfWeek(selectedWeek, selectedYear);
      return {
        startFetchStr: formatDateStr(days[0]),
        endFetchStr: formatDateStr(days[6])
      };
    }
  }, [currentDate, calendarView, selectedWeek, selectedYear]);

  // Cargar registros de producción y cortes
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [prodData, cutoffsData] = await Promise.all([
        predictionService.fetchProductionRecords(startFetchStr, endFetchStr),
        predictionService.fetchServiceCutoffs(startFetchStr, endFetchStr)
      ]);
      setProductionRecords(prodData);
      setServiceCutoffs(cutoffsData);
    } catch (error) {
      console.error('Error fetching calendar and cutoffs data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [startFetchStr, endFetchStr]);

  // Mapeos indexados para búsquedas rápidas
  const recordsByDateAndLine = useMemo(() => {
    const map: Record<string, Record<'L1' | 'L3', ProductionRecord>> = {};
    productionRecords.forEach(rec => {
      if (!map[rec.date]) {
        map[rec.date] = {} as Record<'L1' | 'L3', ProductionRecord>;
      }
      map[rec.date][rec.lineId] = rec;
    });
    return map;
  }, [productionRecords]);

  const cutoffsByDate = useMemo(() => {
    const map: Record<string, ServiceCutoff[]> = {};
    serviceCutoffs.forEach(cut => {
      if (!map[cut.date]) {
        map[cut.date] = [];
      }
      map[cut.date].push(cut);
    });
    return map;
  }, [serviceCutoffs]);

  // Días de la semana seleccionada
  const daysOfWeekList = useMemo(() => {
    return getDaysOfWeek(selectedWeek, selectedYear);
  }, [selectedWeek, selectedYear]);

  // Inicializar volúmenes de la semana cuando se abre el planificador semanal
  useEffect(() => {
    if (isWeeklyModalOpen && daysOfWeekList.length > 0) {
      const initial: Record<string, { L1: number; L3: number }> = {};
      daysOfWeekList.forEach(day => {
        const dateStr = formatDateStr(day);
        const dayRecords = recordsByDateAndLine[dateStr];
        initial[dateStr] = {
          L1: dayRecords?.L1?.projectedVolumeHl || 0,
          L3: dayRecords?.L3?.projectedVolumeHl || 0
        };
      });
      setWeeklyVolumes(initial);
    }
  }, [isWeeklyModalOpen, daysOfWeekList, recordsByDateAndLine]);

  // Consumos reales medidos
  const realConsumptionsByDate = useMemo(() => {
    const map: Record<string, { electricity: number; water: number; steam: number; co2: number }> = {};
    
    stopRecords.forEach(record => {
      if (!record.endTime || !record.endValue) return;
      const dateStr = new Date(record.startTime).toISOString().split('T')[0];
      const consumption = record.endValue - record.startValue;
      const meter = meters.find(m => m.id === record.meterId);
      
      if (!meter) return;
      
      if (!map[dateStr]) {
        map[dateStr] = { electricity: 0, water: 0, steam: 0, co2: 0 };
      }

      if (meter.type === MeterType.ELECTRICITY) {
        map[dateStr].electricity += consumption;
      } else if (meter.type === MeterType.WATER) {
        map[dateStr].water += consumption;
      } else if (meter.type === MeterType.VAPOR) {
        map[dateStr].steam += consumption;
      } else if (meter.type === MeterType.CO2) {
        map[dateStr].co2 += consumption;
      }
    });

    return map;
  }, [stopRecords, meters]);

  // Navegación
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handlePrevWeek = () => {
    if (selectedWeek === 1) {
      setSelectedWeek(53);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedWeek(prev => prev - 1);
    }
  };

  const handleNextWeek = () => {
    if (selectedWeek === 53) {
      setSelectedWeek(1);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedWeek(prev => prev + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedWeek(getWeekNumber(now));
    setSelectedYear(now.getFullYear());
  };

  // Abrir Modal de Edición Diaria
  const handleOpenDayModal = (day: Date) => {
    setSelectedDay(day);
    const dateStr = formatDateStr(day);
    const dayRecords = recordsByDateAndLine[dateStr];
    
    if (dayRecords) {
      if (dayRecords.L1) {
        setL1Projected(dayRecords.L1.projectedVolumeHl);
        setL1Real(dayRecords.L1.realVolumeHl);
        setL1PlannedEvents(dayRecords.L1.plannedEvents);
        setL1RealEvents(dayRecords.L1.realEvents);
      } else {
        setL1Projected(0); setL1Real(0); setL1PlannedEvents([]); setL1RealEvents([]);
      }
      
      if (dayRecords.L3) {
        setL3Projected(dayRecords.L3.projectedVolumeHl);
        setL3Real(dayRecords.L3.realVolumeHl);
        setL3PlannedEvents(dayRecords.L3.plannedEvents);
        setL3RealEvents(dayRecords.L3.realEvents);
      } else {
        setL3Projected(0); setL3Real(0); setL3PlannedEvents([]); setL3RealEvents([]);
      }
    } else {
      setL1Projected(0); setL1Real(0); setL1PlannedEvents([]); setL1RealEvents([]);
      setL3Projected(0); setL3Real(0); setL3PlannedEvents([]); setL3RealEvents([]);
    }
    
    setIsModalOpen(true);
  };

  // Guardar datos de producción real
  const handleSaveProduction = async () => {
    if (!selectedDay) return;
    
    setIsSaving(true);
    const dateStr = formatDateStr(selectedDay);
    
    const newL1Record: ProductionRecord = {
      date: dateStr,
      lineId: 'L1',
      projectedVolumeHl: l1Projected,
      plannedEvents: l1PlannedEvents,
      realVolumeHl: l1Real,
      realEvents: l1RealEvents
    };

    const newL3Record: ProductionRecord = {
      date: dateStr,
      lineId: 'L3',
      projectedVolumeHl: l3Projected,
      plannedEvents: l3PlannedEvents,
      realVolumeHl: l3Real,
      realEvents: l3RealEvents
    };

    try {
      await Promise.all([
        predictionService.saveProductionRecord(newL1Record),
        predictionService.saveProductionRecord(newL3Record)
      ]);
      
      setProductionRecords(prev => {
        const filtered = prev.filter(r => r.date !== dateStr);
        return [...filtered, newL1Record, newL3Record];
      });
      
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving production records:', error);
      alert('Error al guardar la producción');
    } finally {
      setIsSaving(false);
    }
  };

  // Guardar volúmenes semanales
  const handleSaveWeeklyVolumes = async () => {
    setIsSaving(true);
    try {
      const promises: Promise<any>[] = [];
      const newRecordsToAppend: ProductionRecord[] = [];

      Object.keys(weeklyVolumes).forEach((dateStr) => {
        const vols = weeklyVolumes[dateStr];
        const currentL1 = recordsByDateAndLine[dateStr]?.L1;
        const currentL3 = recordsByDateAndLine[dateStr]?.L3;

        const recordL1: ProductionRecord = {
          date: dateStr,
          lineId: 'L1',
          projectedVolumeHl: vols.L1,
          plannedEvents: currentL1?.plannedEvents || [],
          realVolumeHl: currentL1?.realVolumeHl || 0,
          realEvents: currentL1?.realEvents || []
        };

        const recordL3: ProductionRecord = {
          date: dateStr,
          lineId: 'L3',
          projectedVolumeHl: vols.L3,
          plannedEvents: currentL3?.plannedEvents || [],
          realVolumeHl: currentL3?.realVolumeHl || 0,
          realEvents: currentL3?.realEvents || []
        };

        promises.push(predictionService.saveProductionRecord(recordL1));
        promises.push(predictionService.saveProductionRecord(recordL3));
        
        newRecordsToAppend.push(recordL1, recordL3);
      });

      await Promise.all(promises);

      setProductionRecords(prev => {
        const datesToFilter = Object.keys(weeklyVolumes);
        const filtered = prev.filter(r => !datesToFilter.includes(r.date));
        return [...filtered, ...newRecordsToAppend];
      });
      alert('Volúmenes proyectados semanales actualizados con éxito.');
    } catch (error) {
      console.error('Error saving weekly volumes:', error);
      alert('Error al guardar los volúmenes semanales');
    } finally {
      setIsSaving(false);
    }
  };

  // Guardar corte de servicio (desde modal)
  const handleSaveCutoff = async (payload: {
    date: string;
    lineId: 'L1' | 'L3' | 'Elaboración';
    serviceType: 'electricity' | 'water' | 'steam' | 'co2';
    plannedStartTime: string;
    plannedEndTime: string;
    cutoffReason: string;
  }) => {
    const cutoffPayload: Omit<ServiceCutoff, 'id'> = {
      date: payload.date,
      lineId: payload.lineId,
      serviceType: payload.serviceType,
      plannedStartTime: payload.plannedStartTime,
      plannedEndTime: payload.plannedEndTime,
      cutoffReason: payload.cutoffReason,
      isConfirmed: false
    };

    setIsSaving(true);
    try {
      await predictionService.saveServiceCutoff(cutoffPayload);
      const updated = await predictionService.fetchServiceCutoffs(startFetchStr, endFetchStr);
      setServiceCutoffs(updated);
    } catch (error) {
      console.error('Error saving service cutoff:', error);
      alert('Error al programar corte de servicio');
    } finally {
      setIsSaving(false);
    }
  };

  // Confirmar corte real ejecutado
  const handleConfirmCutoff = async (cutoff: ServiceCutoff, actualStart: string, actualEnd: string, comment: string) => {
    if (!cutoff.id) return;
    
    const updatedCutoff: ServiceCutoff = {
      ...cutoff,
      isConfirmed: true,
      actualStartTime: actualStart || cutoff.plannedStartTime,
      actualEndTime: actualEnd || cutoff.plannedEndTime,
      operatorComment: comment,
      confirmedBy: currentUser?.name || 'Operador',
      confirmedAt: new Date().toISOString()
    };

    setIsSaving(true);
    try {
      await predictionService.saveServiceCutoff(updatedCutoff);
      setServiceCutoffs(prev => prev.map(c => c.id === cutoff.id ? updatedCutoff : c));
    } catch (error) {
      console.error('Error confirming cutoff:', error);
      alert('Error al registrar ejecución del corte');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancelar/Revertir confirmación de corte
  const handleCancelConfirmation = async (cutoff: ServiceCutoff) => {
    if (!cutoff.id) return;

    const revertedCutoff: ServiceCutoff = {
      ...cutoff,
      isConfirmed: false,
      actualStartTime: undefined,
      actualEndTime: undefined,
      operatorComment: undefined,
      confirmedBy: undefined,
      confirmedAt: undefined
    };

    setIsSaving(true);
    try {
      await predictionService.saveServiceCutoff(revertedCutoff);
      setServiceCutoffs(prev => prev.map(c => c.id === cutoff.id ? revertedCutoff : c));
    } catch (error) {
      console.error('Error reverting cutoff:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Eliminar corte planificado
  const handleDeleteCutoff = async (cutoffId: string) => {
    if (!confirm('¿Seguro que deseas eliminar este corte programado?')) return;
    
    setIsSaving(true);
    try {
      await predictionService.deleteServiceCutoff(cutoffId);
      setServiceCutoffs(prev => prev.filter(c => c.id !== cutoffId));
    } catch (error) {
      console.error('Error deleting cutoff:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Generar lista de días a mostrar en el calendario
  const calendarDaysList = useMemo(() => {
    if (calendarView === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDayIndex = new Date(year, month, 1).getDay();
      const totalDays = new Date(year, month + 1, 0).getDate();
      const arr: (Date | null)[] = [];
      for (let i = 0; i < firstDayIndex; i++) {
        arr.push(null);
      }
      for (let i = 1; i <= totalDays; i++) {
        arr.push(new Date(year, month, i));
      }
      return arr;
    } else {
      return daysOfWeekList;
    }
  }, [currentDate, calendarView, daysOfWeekList]);

  // Datos para el gráfico compuesto
  const chartData = useMemo(() => {
    const result: any[] = [];
    const pad = (n: number) => n.toString().padStart(2, '0');

    if (chartViewMode === 'day') {
      const dateStr = chartDayFocus;
      const dayRecords = recordsByDateAndLine[dateStr];
      const dayCutoffs = cutoffsByDate[dateStr] || [];
      const realCons = realConsumptionsByDate[dateStr] || { electricity: 0, water: 0, steam: 0, co2: 0 };
      
      const L1Proj = dayRecords?.L1?.projectedVolumeHl || 0;
      const L3Proj = dayRecords?.L3?.projectedVolumeHl || 0;
      const L1Real = dayRecords?.L1?.realVolumeHl || 0;
      const L3Real = dayRecords?.L3?.realVolumeHl || 0;
      
      const L1Events = dayRecords?.L1?.plannedEvents || [];
      const L3Events = dayRecords?.L3?.plannedEvents || [];
      
      const L1Coeff = LINE_COEFFICIENTS.L1[selectedService];
      const L3Coeff = LINE_COEFFICIENTS.L3[selectedService];
      
      let dailyPredTotal = 0;
      const hourlyPreds: number[] = [];
      
      for (let h = 0; h < 24; h++) {
        const baseH = (L1Coeff.base + L3Coeff.base) / 24;
        let prodH = 0;
        if (h >= 6 && h < 22) {
          prodH += (L1Proj / 16) * L1Coeff.perHl;
          prodH += (L3Proj / 16) * L3Coeff.perHl;
          
          L1Events.forEach(ev => {
            if (ev === 'CIP') prodH += L1Coeff.cip / 16;
            if (ev === 'Cambio Formato') prodH += L1Coeff.formatChange / 16;
            if (ev === 'Mantenimiento') prodH += L1Coeff.maint / 16;
          });
          L3Events.forEach(ev => {
            if (ev === 'CIP') prodH += L3Coeff.cip / 16;
            if (ev === 'Cambio Formato') prodH += L3Coeff.formatChange / 16;
            if (ev === 'Mantenimiento') prodH += L3Coeff.maint / 16;
          });
        }
        
        let predHVal = baseH + prodH;
        
        const isCutActive = dayCutoffs.some(c => {
          if (c.serviceType !== selectedService) return false;
          const start = Number(c.plannedStartTime.split(':')[0]);
          const end = Number(c.plannedEndTime.split(':')[0]);
          return h >= start && h < end;
        });
        
        if (isCutActive) {
          predHVal = baseH * 0.05;
        }
        
        hourlyPreds.push(predHVal);
        dailyPredTotal += predHVal;
      }
      
      const dayRealConsValue = selectedService === 'electricity' ? realCons.electricity 
        : selectedService === 'water' ? realCons.water 
        : selectedService === 'steam' ? realCons.steam 
        : realCons.co2;

      for (let h = 0; h < 24; h++) {
        const hourStr = `${h.toString().padStart(2, '0')}:00`;
        const predH = hourlyPreds[h];
        
        const activeCut = dayCutoffs.find(c => {
          if (c.serviceType !== selectedService) return false;
          const start = Number(c.plannedStartTime.split(':')[0]);
          const end = Number(c.plannedEndTime.split(':')[0]);
          return h >= start && h < end;
        });

        let realH = 0;
        if (dayRealConsValue > 0) {
          const factor = dailyPredTotal > 0 ? (predH / dailyPredTotal) : 1 / 24;
          const variation = 1 + (Math.sin(h) * 0.08);
          realH = dayRealConsValue * factor * variation;
          
          if (activeCut) {
            if (activeCut.isConfirmed) {
              realH = ((L1Coeff.base + L3Coeff.base) / 24) * 0.05;
            } else {
              realH = predH * 1.35;
            }
          }
        } else {
          realH = 0;
        }

        let realVolH = 0;
        if (h >= 6 && h < 22 && (L1Real > 0 || L3Real > 0)) {
          realVolH = (L1Real + L3Real) / 16;
        }
        
        let planVolH = 0;
        if (h >= 6 && h < 22 && (L1Proj > 0 || L3Proj > 0)) {
          planVolH = (L1Proj + L3Proj) / 16;
        }

        result.push({
          day: hourStr,
          date: dateStr,
          'Prod. Proyectada (hl)': Number(planVolH.toFixed(1)),
          'Prod. Real (hl)': Number(realVolH.toFixed(1)),
          predConsumo: Number(predH.toFixed(2)),
          realConsumo: Number(realH.toFixed(2)),
          corteHoras: activeCut ? 1 : 0
        });
      }
    } else if (chartViewMode === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const totalDays = new Date(year, month + 1, 0).getDate();

      for (let day = 1; day <= totalDays; day++) {
        const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
        const dayRecords = recordsByDateAndLine[dateStr];
        const dayCutoffs = cutoffsByDate[dateStr] || [];
        const realCons = realConsumptionsByDate[dateStr] || { electricity: 0, water: 0, steam: 0, co2: 0 };
        
        let totalProjectedVolume = 0;
        let totalRealVolume = 0;
        
        const cutoffHours = { electricity: 0, water: 0, steam: 0, co2: 0 };
        dayCutoffs.forEach(c => {
          cutoffHours[c.serviceType] += getDurationInHours(c.plannedStartTime, c.plannedEndTime);
        });

        let predElectricity = 0;
        let predWater = 0;
        let predSteam = 0;
        let predCo2 = 0;

        if (dayRecords) {
          if (dayRecords.L1) {
            totalProjectedVolume += dayRecords.L1.projectedVolumeHl;
            totalRealVolume += dayRecords.L1.realVolumeHl;
            const predL1 = predictionService.calculatePrediction('L1', dayRecords.L1.projectedVolumeHl, dayRecords.L1.plannedEvents, cutoffHours);
            predElectricity += predL1.electricityKwh;
            predWater += predL1.waterM3;
            predSteam += predL1.steamTn;
            predCo2 += predL1.co2Tn;
          }
          if (dayRecords.L3) {
            totalProjectedVolume += dayRecords.L3.projectedVolumeHl;
            totalRealVolume += dayRecords.L3.realVolumeHl;
            const predL3 = predictionService.calculatePrediction('L3', dayRecords.L3.projectedVolumeHl, dayRecords.L3.plannedEvents, cutoffHours);
            predElectricity += predL3.electricityKwh;
            predWater += predL3.waterM3;
            predSteam += predL3.steamTn;
            predCo2 += predL3.co2Tn;
          }
        }

        if (totalProjectedVolume > 0 || totalRealVolume > 0 || realCons.electricity > 0 || dayCutoffs.length > 0) {
          result.push({
            day: `${day}`,
            date: dateStr,
            'Prod. Proyectada (hl)': totalProjectedVolume,
            'Prod. Real (hl)': totalRealVolume,
            predConsumo: Number((selectedService === 'electricity' ? predElectricity 
              : selectedService === 'water' ? predWater 
              : selectedService === 'steam' ? predSteam 
              : predCo2).toFixed(2)),
            realConsumo: Number((selectedService === 'electricity' ? realCons.electricity 
              : selectedService === 'water' ? realCons.water 
              : selectedService === 'steam' ? realCons.steam 
              : realCons.co2).toFixed(2)),
            corteHoras: cutoffHours[selectedService]
          });
        }
      }
    } else {
      daysOfWeekList.forEach(day => {
        const dateStr = formatDateStr(day);
        const dayRecords = recordsByDateAndLine[dateStr];
        const dayCutoffs = cutoffsByDate[dateStr] || [];
        const realCons = realConsumptionsByDate[dateStr] || { electricity: 0, water: 0, steam: 0, co2: 0 };
        
        let totalProjectedVolume = 0;
        let totalRealVolume = 0;

        const cutoffHours = { electricity: 0, water: 0, steam: 0, co2: 0 };
        dayCutoffs.forEach(c => {
          cutoffHours[c.serviceType] += getDurationInHours(c.plannedStartTime, c.plannedEndTime);
        });

        let predElectricity = 0;
        let predWater = 0;
        let predSteam = 0;
        let predCo2 = 0;

        if (dayRecords) {
          if (dayRecords.L1) {
            totalProjectedVolume += dayRecords.L1.projectedVolumeHl;
            totalRealVolume += dayRecords.L1.realVolumeHl;
            const predL1 = predictionService.calculatePrediction('L1', dayRecords.L1.projectedVolumeHl, dayRecords.L1.plannedEvents, cutoffHours);
            predElectricity += predL1.electricityKwh;
            predWater += predL1.waterM3;
            predSteam += predL1.steamTn;
            predCo2 += predL1.co2Tn;
          }
          if (dayRecords.L3) {
            totalProjectedVolume += dayRecords.L3.projectedVolumeHl;
            totalRealVolume += dayRecords.L3.realVolumeHl;
            const predL3 = predictionService.calculatePrediction('L3', dayRecords.L3.projectedVolumeHl, dayRecords.L3.plannedEvents, cutoffHours);
            predElectricity += predL3.electricityKwh;
            predWater += predL3.waterM3;
            predSteam += predL3.steamTn;
            predCo2 += predL3.co2Tn;
          }
        }

        const daysNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        result.push({
          day: `${daysNames[day.getDay()]} ${day.getDate()}`,
          date: dateStr,
          'Prod. Proyectada (hl)': totalProjectedVolume,
          'Prod. Real (hl)': totalRealVolume,
          predConsumo: Number((selectedService === 'electricity' ? predElectricity 
            : selectedService === 'water' ? predWater 
            : selectedService === 'steam' ? predSteam 
            : predCo2).toFixed(2)),
          realConsumo: Number((selectedService === 'electricity' ? realCons.electricity 
            : selectedService === 'water' ? realCons.water 
            : selectedService === 'steam' ? realCons.steam 
            : realCons.co2).toFixed(2)),
          corteHoras: cutoffHours[selectedService]
        });
      });
    }
    
    return result;
  }, [currentDate, chartViewMode, chartDayFocus, daysOfWeekList, productionRecords, serviceCutoffs, realConsumptionsByDate, selectedService]);

  const getServiceLabel = () => {
    if (selectedService === 'electricity') return 'Electricidad (kWh)';
    if (selectedService === 'water') return 'Agua (m³)';
    if (selectedService === 'steam') return 'Vapor (TN)';
    return 'CO2 (TN)';
  };

  // Cortes semanales para el planificador semanal
  const weeklyCutoffs = useMemo(() => {
    const dates = daysOfWeekList.map(formatDateStr);
    return serviceCutoffs.filter(c => dates.includes(c.date));
  }, [serviceCutoffs, daysOfWeekList]);

  const selectedDayCutoffs = useMemo(() => {
    if (!selectedDay) return [];
    return serviceCutoffs.filter(c => c.date === formatDateStr(selectedDay));
  }, [selectedDay, serviceCutoffs]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Cabecera */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <CalendarDaysIcon className="h-8 w-8 text-[#E2B53F]" />
            Calendario de Producción y Modulación
          </h1>
          <p className="text-gray-500 mt-1">Planifica la producción de L1/L3, gestiona los cortes de suministro y predice consumos.</p>
        </div>

        {/* Controles de selección de Vista principal */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-100 p-1.5 rounded-xl w-full xl:w-auto">
          <button
            onClick={() => { setActiveTab('calendar'); setCalendarView('month'); }}
            className={`flex-1 sm:flex-initial px-4 py-2 text-sm font-bold rounded-lg transition-all ${
              activeTab === 'calendar' && calendarView === 'month'
                ? 'bg-white shadow-sm text-[#E2B53F]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Modo Mes
          </button>
          
          <button
            onClick={() => { setActiveTab('calendar'); setCalendarView('week'); }}
            className={`flex-1 sm:flex-initial px-4 py-2 text-sm font-bold rounded-lg transition-all ${
              activeTab === 'calendar' && calendarView === 'week'
                ? 'bg-white shadow-sm text-[#E2B53F]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Modo Semana
          </button>
          
          <button
            onClick={() => setActiveTab('charts')}
            className={`flex-1 sm:flex-initial px-4 py-2 text-sm font-bold rounded-lg transition-all ${
              activeTab === 'charts' 
                ? 'bg-white shadow-sm text-[#E2B53F]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Gráfico de Modulación
          </button>
        </div>
      </div>

      {activeTab === 'calendar' ? (
        <Card className="p-6 shadow-sm border border-gray-100">
          <CalendarHeader 
            currentDate={currentDate}
            calendarView={calendarView}
            setCalendarView={setCalendarView}
            selectedWeek={selectedWeek}
            setSelectedWeek={setSelectedWeek}
            selectedYear={selectedYear}
            handlePrev={calendarView === 'month' ? handlePrevMonth : handlePrevWeek}
            handleNext={calendarView === 'month' ? handleNextMonth : handleNextWeek}
            handleToday={handleToday}
            setIsWeeklyModalOpen={setIsWeeklyModalOpen}
          />

          {/* VISTA MODO MES */}
          {calendarView === 'month' && (
            <>
              <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                <div>Dom</div><div>Lun</div><div>Mar</div><div>Mié</div><div>Jue</div><div>Vie</div><div>Sáb</div>
              </div>

              {isLoading ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E2B53F]"></div>
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-2">
                  {calendarDaysList.map((day, idx) => {
                    if (!day) {
                      return <div key={`empty-${idx}`} className="h-16 sm:h-28 bg-gray-50/50 rounded-xl border border-dashed border-gray-100"></div>;
                    }

                    const dateStr = formatDateStr(day);
                    const dayRecords = recordsByDateAndLine[dateStr];
                    const dayCutoffs = serviceCutoffs.filter(c => c.date === dateStr);
                    const hasCuts = dayCutoffs.length > 0;
                    const allCutsConfirmed = hasCuts && dayCutoffs.every(c => c.isConfirmed);
                    
                    const hasL1 = !!dayRecords?.L1;
                    const hasL3 = !!dayRecords?.L3;
                    const isToday = new Date().toDateString() === day.toDateString();

                    let cardBgClass = 'bg-slate-50 border-slate-300 text-slate-900 hover:bg-slate-100 hover:border-slate-400';
                    if (isToday) {
                      cardBgClass = 'bg-amber-50/30 border-amber-500 text-slate-900 hover:bg-amber-50/50 hover:border-amber-600 ring-1 ring-amber-500/10';
                    } else if (hasCuts) {
                      cardBgClass = allCutsConfirmed 
                        ? 'bg-emerald-50 border-emerald-400 text-slate-900 hover:bg-emerald-100/70 hover:border-emerald-500' 
                        : 'bg-rose-50 border-rose-400 text-slate-900 hover:bg-rose-100/70 hover:border-rose-500';
                    }

                    return (
                      <div
                        key={dateStr}
                        onClick={() => handleOpenDayModal(day)}
                        className={`h-16 sm:h-28 p-1.5 sm:p-2 rounded-xl border transition-all cursor-pointer flex flex-col justify-between hover:shadow-md ${cardBgClass}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-black text-slate-900 flex items-center gap-1">
                            {day.getDate()}
                            {isToday && <span className="h-1.5 w-1.5 bg-amber-500 rounded-full inline-block" />}
                          </span>
                          {hasCuts && (
                            <span className="flex items-center">
                              {allCutsConfirmed ? <SuccessCheckSVG /> : <PendingAlertSVG />}
                            </span>
                          )}
                        </div>

                        {/* Producción */}
                        <div className="hidden sm:block space-y-1">
                          {hasL1 && (dayRecords.L1.projectedVolumeHl > 0 || dayRecords.L1.realVolumeHl > 0) && (
                            <div className="text-[9px] flex justify-between bg-white/60 border border-black/5 rounded px-1 text-gray-800 font-bold">
                              <span>L1</span>
                              <span>{dayRecords.L1.realVolumeHl || dayRecords.L1.projectedVolumeHl} hl</span>
                            </div>
                          )}
                          {hasL3 && (dayRecords.L3.projectedVolumeHl > 0 || dayRecords.L3.realVolumeHl > 0) && (
                            <div className="text-[9px] flex justify-between bg-white/60 border border-black/5 rounded px-1 text-gray-800 font-bold">
                              <span>L3</span>
                              <span>{dayRecords.L3.realVolumeHl || dayRecords.L3.projectedVolumeHl} hl</span>
                            </div>
                          )}
                        </div>

                        {/* Eventos */}
                        <div className="hidden sm:flex items-center justify-between min-h-[16px]">
                          <div className="flex space-x-0.5 overflow-hidden">
                            {hasL1 && dayRecords.L1.plannedEvents.map(e => (
                              <span key={e} className="inline-block p-0.5 bg-white/80 rounded text-slate-600 border border-slate-100" title={`L1 Plan: ${e}`}>
                                <EventIcon name={e} className="h-2.5 w-2.5" />
                              </span>
                            ))}
                            {hasL3 && dayRecords.L3.plannedEvents.map(e => (
                              <span key={e} className="inline-block p-0.5 bg-white/80 rounded text-slate-600 border border-slate-100" title={`L3 Plan: ${e}`}>
                                <EventIcon name={e} className="h-2.5 w-2.5" />
                              </span>
                            ))}
                          </div>
                          {hasCuts && (
                            <div className="flex gap-0.5">
                              {dayCutoffs.map(c => (
                                <span 
                                  key={c.id} 
                                  className={`p-0.5 rounded border ${c.isConfirmed ? 'bg-emerald-100 border-emerald-200 text-emerald-800' : 'bg-rose-100 border-rose-200 text-rose-800'}`}
                                  title={`${c.lineId} ${c.serviceType}: ${c.plannedStartTime}`}
                                >
                                  <ServiceIcon type={c.serviceType} className="h-2.5 w-2.5" />
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* VISTA MODO SEMANA */}
          {calendarView === 'week' && (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
              {isLoading ? (
                <div className="col-span-full h-[400px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E2B53F]"></div>
                </div>
              ) : (
                calendarDaysList.map((day) => {
                  if (!day) return null;
                  const dateStr = formatDateStr(day);
                  const dayRecords = recordsByDateAndLine[dateStr];
                  const dayCutoffs = serviceCutoffs.filter(c => c.date === dateStr);
                  const hasCuts = dayCutoffs.length > 0;
                  const allCutsConfirmed = hasCuts && dayCutoffs.every(c => c.isConfirmed);
                  
                  const hasL1 = !!dayRecords?.L1;
                  const hasL3 = !!dayRecords?.L3;
                  const isToday = new Date().toDateString() === day.toDateString();
                  const daysNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

                  let cardBgClass = 'bg-slate-50 border-slate-300 text-slate-900 hover:bg-slate-100 hover:border-slate-400';
                  if (isToday) {
                    cardBgClass = 'bg-amber-50/30 border-amber-500 text-slate-900 hover:bg-amber-50/50 hover:border-amber-600 ring-1 ring-amber-500/10';
                  } else if (hasCuts) {
                    cardBgClass = allCutsConfirmed 
                      ? 'bg-emerald-50 border-emerald-400 text-slate-900 hover:bg-emerald-100/70 hover:border-emerald-500' 
                      : 'bg-rose-50 border-rose-400 text-slate-900 hover:bg-rose-100/70 hover:border-rose-500';
                  }

                  return (
                    <div
                      key={dateStr}
                      onClick={() => handleOpenDayModal(day)}
                      className={`min-h-[260px] p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between hover:shadow-lg ${cardBgClass}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{daysNames[day.getDay()]}</p>
                          <p className="text-xl font-black mt-0.5 text-slate-900 flex items-center gap-1.5">
                            {day.getDate()}
                            {isToday && <span className="h-2 w-2 bg-amber-500 rounded-full inline-block" />}
                          </p>
                        </div>
                        {hasCuts && (
                          <div className="mt-1">
                            {allCutsConfirmed ? (
                              <span className="inline-flex items-center justify-center p-1.5 bg-emerald-100 text-emerald-800 rounded-full border border-emerald-200 shadow-sm" title="Cortes cerrados">
                                <SuccessCheckSVG className="h-3 w-3" />
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center p-1.5 bg-rose-100 text-rose-800 rounded-full border border-rose-200 shadow-sm" title="Cortes pendientes">
                                <PendingAlertSVG className="h-3 w-3" />
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Producción */}
                      <div className="space-y-1.5 my-3">
                        {hasL1 && (dayRecords.L1.projectedVolumeHl > 0 || dayRecords.L1.realVolumeHl > 0) && (
                          <div className="text-[11px] bg-white/70 border border-black/5 rounded-lg p-1.5 flex justify-between font-bold text-slate-800">
                            <span>L1 (Cerveza)</span>
                            <span className="font-extrabold text-slate-900">{dayRecords.L1.realVolumeHl || dayRecords.L1.projectedVolumeHl} hl</span>
                          </div>
                        )}
                        {hasL3 && (dayRecords.L3.projectedVolumeHl > 0 || dayRecords.L3.realVolumeHl > 0) && (
                          <div className="text-[11px] bg-white/70 border border-black/5 rounded-lg p-1.5 flex justify-between font-bold text-slate-800">
                            <span>L3 (Cerveza)</span>
                            <span className="font-extrabold text-slate-900">{dayRecords.L3.realVolumeHl || dayRecords.L3.projectedVolumeHl} hl</span>
                          </div>
                        )}
                      </div>

                      {/* Detalles inferiores */}
                      <div className="space-y-2 border-t border-slate-200/50 pt-2 text-[10px]">
                        {/* Eventos */}
                        <div className="flex flex-wrap gap-1 min-h-[14px]">
                          {hasL1 && dayRecords.L1.plannedEvents.map(e => (
                            <span key={e} className="inline-flex items-center gap-0.5 bg-white/80 border border-slate-200 px-1 rounded font-bold text-slate-600" title={`L1 Plan: ${e}`}>
                              <EventIcon name={e} className="h-2.5 w-2.5" /> L1
                            </span>
                          ))}
                          {hasL3 && dayRecords.L3.plannedEvents.map(e => (
                            <span key={e} className="inline-flex items-center gap-0.5 bg-white/80 border border-slate-200 px-1 rounded font-bold text-slate-600" title={`L3 Plan: ${e}`}>
                              <EventIcon name={e} className="h-2.5 w-2.5" /> L3
                            </span>
                          ))}
                        </div>

                        {/* Listado de cortes en formato horizontal */}
                        {hasCuts && (
                          <div className="flex flex-col gap-1">
                            {dayCutoffs.map(c => (
                              <span 
                                key={c.id} 
                                className={`px-2 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider flex items-center justify-between gap-1 shadow-sm ${
                                  c.isConfirmed 
                                    ? 'bg-emerald-50 border-emerald-250 text-emerald-800' 
                                    : 'bg-rose-50 border-rose-250 text-rose-800'
                                }`}
                              >
                                <span className="flex items-center gap-1">
                                  <ServiceIcon type={c.serviceType} className="h-2.5 w-2.5" />
                                  <span>{c.serviceType === 'electricity' ? 'Agua' : c.serviceType === 'water' ? 'Agua' : c.serviceType === 'steam' ? 'Vapor' : 'CO2'} - {c.lineId}</span>
                                </span>
                                <span className="font-extrabold text-[8px]">{c.plannedStartTime}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          <ModulationChartCard
            chartData={chartData}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            chartViewMode={chartViewMode}
            setChartViewMode={setChartViewMode}
            chartDayFocus={chartDayFocus}
            setChartDayFocus={setChartDayFocus}
            getServiceLabel={getServiceLabel}
            calendarView={calendarView}
          />
          <KpiAnalysisTable
            chartData={chartData}
            selectedService={selectedService}
            chartViewMode={chartViewMode}
            chartDayFocus={chartDayFocus}
            currentDate={currentDate}
            formatDateStr={formatDateStr}
            getServiceLabel={getServiceLabel}
          />
        </div>
      )}

      {/* MODAL DIARIO (ShiftDetailModal) */}
      <ShiftDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedDay={selectedDay || new Date()}
        selectedDayCutoffs={selectedDayCutoffs}
        isSaving={isSaving}
        isEditor={isEditor}
        l1Projected={l1Projected}
        l1Real={l1Real}
        setL1Real={setL1Real}
        l3Projected={l3Projected}
        l3Real={l3Real}
        setL3Real={setL3Real}
        l1PlannedEvents={l1PlannedEvents}
        l1RealEvents={l1RealEvents}
        setL1RealEvents={setL1RealEvents}
        l3PlannedEvents={l3PlannedEvents}
        l3RealEvents={l3RealEvents}
        setL3RealEvents={setL3RealEvents}
        onSaveProduction={handleSaveProduction}
        onConfirmCutoff={handleConfirmCutoff}
        onCancelConfirmation={handleCancelConfirmation}
      />

      {/* PLANIFICADOR SEMANAL (WeeklyPlannerModal) */}
      <WeeklyPlannerModal
        isOpen={isWeeklyModalOpen}
        onClose={() => setIsWeeklyModalOpen(false)}
        selectedWeek={selectedWeek}
        selectedYear={selectedYear}
        isSaving={isSaving}
        isEditor={isEditor}
        weeklyVolumes={weeklyVolumes}
        setWeeklyVolumes={setWeeklyVolumes}
        onSaveWeeklyVolumes={handleSaveWeeklyVolumes}
        weeklyCutoffs={weeklyCutoffs}
        onSaveCutoff={handleSaveCutoff}
        onDeleteCutoff={handleDeleteCutoff}
        daysOfWeekList={daysOfWeekList}
      />
    </div>
  );
};
